// src/pages/TestAPI.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiSend } from 'react-icons/fi';
import feather from 'feather-icons';
import Cookies from "js-cookie";

export default function TestAPI() {

  // Redirect if no token
  useEffect(() => {
    const t = Cookies.get("authToken");
    if (!t) window.location.href = "/";
  }, []);

  // Helper for auth headers
  const getAuthHeaders = useCallback((extra = {}) => {
    const token = Cookies.get("authToken");
    const base = token ? { Authorization: `Bearer ${token}` } : {};
    return { ...base, ...extra };
  }, []);

  // UI States
  const [logEntries, setLogEntries] = useState([]);

  const [categories, setCategories] = useState([]);
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [parameters, setParameters] = useState([]);

  const [formData, setFormData] = useState({
    category: '',
    recipient_phone: '',
    template_name: '',
    language_code: 'en_US'
  });

  const [mediaOptions, setMediaOptions] = useState([]);
  const [mediaFormData, setMediaFormData] = useState({
    recipient_phone_number: '',
    media_name: '',
    media_type: ''
  });

  // Load feather icons once
  useEffect(() => {
    feather.replace();
  }, []);

  // ----------------------------
  // Fetch Functions (Stable)
  // ----------------------------

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetch('/api/api/notification-categories', {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) setCategories(Object.keys(data.categories));
    } catch (e) {
      console.error("Failed loading categories:", e);
    }
  }, [getAuthHeaders]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch('/api/api/templates?status=APPROVED', {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) setApprovedTemplates(data.templates);
    } catch (e) {
      console.error("Failed loading templates:", e);
    }
  }, [getAuthHeaders]);

  const loadMediaOptions = useCallback(async () => {
    try {
      const res = await fetch('/api/api/media/names', {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setMediaOptions(data);
    } catch (e) {
      console.error("Failed loading media options:", e);
    }
  }, [getAuthHeaders]);

  // Load all once
  useEffect(() => {
    loadCategories();
    loadTemplates();
    loadMediaOptions();
  }, [loadCategories, loadTemplates, loadMediaOptions]);

  // ----------------------------
  // Template Tester Handlers
  // ----------------------------

  const handleTemplateChange = (name) => {
    const tpl = approvedTemplates.find(t => t.name === name);

    setFormData(f => ({
      ...f,
      template_name: name,
      language_code: tpl?.language || f.language_code
    }));

    const bodyText = tpl?.components?.find(c => c.type.toLowerCase() === 'body')?.text || '';
    const keys = [...new Set(
      (bodyText.match(/\{\{(\d+)\}\}/g) || []).map(m => m.slice(2, -2))
    )];

    setParameters(keys.map(key => ({ key, value: '' })));
  };

  const updateParameter = (i, value) => {
    setParameters(params => {
      const updated = [...params];
      updated[i].value = value;
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      template_params: parameters.reduce((acc, p) => {
        acc[p.key] = p.value;
        return acc;
      }, {})
    };

    try {
      const res = await fetch('/api/api/send_message', {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      appendLog(data.success ? 'success' : 'error', data);

    } catch (err) {
      appendLog('error', err);
    }
  };

  // ----------------------------
  // Media Tester Handlers
  // ----------------------------

  const handleMediaNameChange = (name) => {
    const opt = mediaOptions.find(m => m.name === name);
    setMediaFormData(f => ({
      ...f,
      media_name: name,
      media_type: opt?.type?.toLowerCase() || ''
    }));
  };

  const handleMediaSubmit = async (e) => {
    e.preventDefault();

    try {
      const res = await fetch('/api/api/send_media', {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(mediaFormData)
      });

      const data = await res.json();
      appendLog(data.success ? 'success' : 'error', data);

    } catch (err) {
      appendLog('error', err);
    }
  };

  // ----------------------------
  // Shared Log Utilities
  // ----------------------------

  const appendLog = (type, data) => {
    setLogEntries(logs => [
      ...logs,
      {
        type,
        timestamp: new Date().toLocaleTimeString(),
        data: typeof data === "string" ? data : JSON.stringify(data, null, 2)
      }
    ]);
  };

  const clearLog = () => setLogEntries([]);

  // ----------------------------
  // Render
  // ----------------------------

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mx-auto" style={{ maxWidth: '1800px' }}>

          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            API Testing Center
          </h1>

          {/* ————————————————————————————
              2-column layout
             ———————————————————————————— */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">

            {/* Notification Tester */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100">
              <div className="border-b px-6 py-4 font-semibold text-gray-900">
                WhatsApp Notification Tester
              </div>

              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">

                  {/* Category + Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <select
                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                        value={formData.category}
                        onChange={e => setFormData(f => ({ ...f, category: e.target.value }))}
                        required
                      >
                        <option value="" disabled>Select category</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Recipient Phone</label>
                      <input
                        type="text"
                        placeholder="+1234567890"
                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                        value={formData.recipient_phone}
                        onChange={e => setFormData(f => ({ ...f, recipient_phone: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  {/* Template + Language */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Template Name</label>
                      <select
                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                        value={formData.template_name}
                        onChange={e => handleTemplateChange(e.target.value)}
                        required
                      >
                        <option value="" disabled>Select template</option>
                        {approvedTemplates.map(t => (
                          <option key={t.id} value={t.name}>{t.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Language</label>
                      <select
                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                        value={formData.language_code}
                        onChange={e => setFormData(f => ({ ...f, language_code: e.target.value }))}
                      >
                        <option value="en_US">English (US)</option>
                        <option value="es">Spanish</option>
                        <option value="pt_BR">Portuguese (BR)</option>
                        <option value="hi">Hindi</option>
                      </select>
                    </div>
                  </div>

                  {/* Parameters */}
                  {parameters.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template Parameters
                      </label>
                      <div className="space-y-3">
                        {parameters.map((p, i) => (
                          <input
                            key={i}
                            type="text"
                            className="w-full px-4 py-2 border rounded-lg"
                            placeholder={`Value for {{${p.key}}}`}
                            value={p.value}
                            onChange={e => updateParameter(i, e.target.value)}
                            required
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Submit Btn */}
                  <button
                    type="submit"
                    className="w-full py-3 px-6 bg-[#581745] hover:bg-[#1fa2c9] text-white rounded-lg"
                  >
                    <FiSend className="inline-block mr-2" />
                    Send Test Notification
                  </button>

                </form>
              </div>
            </div>

            {/* Media Tester */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100">
              <div className="border-b px-6 py-4 font-semibold text-gray-900">
                WhatsApp Media Tester
              </div>

              <div className="p-6">
                <form onSubmit={handleMediaSubmit} className="space-y-6">

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Recipient Phone</label>
                      <input
                        type="text"
                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                        placeholder="+1234567890"
                        value={mediaFormData.recipient_phone_number}
                        onChange={e =>
                          setMediaFormData(f => ({ ...f, recipient_phone_number: e.target.value }))
                        }
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">Media Name</label>
                      <select
                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                        value={mediaFormData.media_name}
                        onChange={e => handleMediaNameChange(e.target.value)}
                        required
                      >
                        <option value="" disabled>Select media</option>
                        {mediaOptions.map(opt => (
                          <option key={opt.name} value={opt.name}>{opt.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Media Type</label>
                    <input
                      type="text"
                      className="mt-1 w-full px-4 py-2 bg-gray-100 border rounded-lg"
                      value={mediaFormData.media_type}
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 px-6 bg-[#581745] hover:bg-[#1fa2c9] text-white rounded-lg"
                  >
                    <FiSend className="inline-block mr-2" />
                    Send Test Notification
                  </button>

                </form>
              </div>
            </div>
          </div>

          {/* Response Log */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100">
            <div className="flex justify-between items-center border-b px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Response Log</h2>
              <button onClick={clearLog} className="text-gray-600 hover:text-gray-800 flex items-center">
                <FiRefreshCw className="mr-1" /> Clear
              </button>
            </div>

            <div className="p-6 bg-gray-50 max-h-96 overflow-y-auto space-y-4">
              {logEntries.length === 0 ? (
                <div className="text-gray-400 text-center py-8">No responses yet.</div>
              ) : (
                logEntries.map((e, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border-l-4 ${
                      e.type === 'success'
                        ? 'border-green-500 bg-white'
                        : 'border-red-500 bg-white'
                    }`}
                  >
                    <div className="flex justify-between mb-2">
                      <span className={e.type === 'success' ? 'text-green-600' : 'text-red-600'}>
                        {e.type.charAt(0).toUpperCase() + e.type.slice(1)}
                      </span>
                      <span className="text-sm text-gray-500">{e.timestamp}</span>
                    </div>

                    <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                      {e.data}
                    </pre>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
